import { Box, Button, ButtonGroup, Flex, Icon, Image, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../hooks/listings/useSendListingMessage'
import { useEffect, useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerHasSponsor,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri
} from '../../../store/selectors/customerProfile'
import { MessageTopicKey } from '../../../api/gqlaxy/gql/generated/graphql'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../store/selectors/sponsorship'
import * as Sentry from '@sentry/browser'
import { ConfettiTarget } from '../../shared/Confetti'

const MSG = defineMessages({
  cancel: {
    id: 'AssumablesLearnMoreModal.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'AssumablesLearnMoreModal.confirm',
    defaultMessage: 'Confirm'
  },
  assumablesDm: {
    id: 'AssumablesLearnMoreModal.assumablesDm',
    defaultMessage: 'Ask {name} if you qualify'
  },
  assumablesDmGeneral: {
    id: 'AssumablesLearnMoreModal.assumablesDmGeneral',
    defaultMessage: 'Ask {name} about these listings'
  },
  success: {
    id: 'AssumablesLearnMoreModal.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'AssumablesLearnMoreModal.fail',
    defaultMessage: 'Direct message failed to send.'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

export const AssumablesDirectMessage = () => {
  const intl = useIntl()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const [isSending, setIsSending] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)

  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerSponsored = useSelector(selectCustomerHasSponsor)

  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const handleSubmit = async () => {
    setIsSending(true)

    try {
      await sendListingMessage({
        input: {
          topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
          message: isCustomerREA
            ? 'Can you tell me more about this listing gallery?'
            : 'I want to explore assumable mortgages, is this available to me?',
          topic: 'Assumable Loans'
        }
      })
      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align={['unset', 'center']} maxW={['100%', '25rem']} w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position={'relative'} mb={0} overflow={'hidden'} w={'100%'}>
              <Flex direction={'column'} gap={4} transition={'.15s ease-in-out transform'}>
                <Flex direction='column' gap={4} width='100%'>
                  {dmStatus === DMStatus.Initial && (
                    <Button
                      py='8px'
                      colorScheme='secondary'
                      width='100%'
                      isLoading={isSending}
                      variant={'outline'}
                      bg='neutral.50'
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                      tracking={{
                        guid: 'Assumables Learn More Modal',
                        descriptor: 'Initial Button Press to send message'
                      }}
                      leftIcon={
                        <Image
                          src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                          h={6}
                          w={6}
                          borderRadius='50%'
                        />
                      }
                    >
                      {isCustomerLO || isCustomerSponsored
                        ? intl.formatMessage(MSG.assumablesDm, {
                          name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                        })
                        : intl.formatMessage(MSG.assumablesDmGeneral, { name: customerFirstName })}
                    </Button>
                  )}
                  {dmStatus === DMStatus.Confirmation && (
                    <ButtonGroup width={'100%'}>
                      {!isSending && (
                        <Button
                          variant={'outline'}
                          colorScheme='secondary'
                          bg='neutral.50'
                          w='auto'
                          transition={'.2s ease-in-out all'}
                          px={12}
                          tracking={{
                            guid: 'fHi5iCjZh1nsarWFHHPRf5',
                            ui_context: 'Assumables Learn More Modal',
                            descriptor: 'Cancel submission of dm'
                          }}
                          onClick={() => setDmStatus(DMStatus.Initial)}
                        >
                          {intl.formatMessage(MSG.cancel)}
                        </Button>
                      )}
                      <Button
                        w='100%'
                        onClick={() => handleSubmit()}
                        colorScheme='primary'
                        isLoading={isSending}
                        tracking={{
                          guid: 'sXCKvqYN7iQnoys7tbKGwL',
                          ui_context: 'Assumables Learn More Modal',
                          descriptor: 'Confirm direct message submission'
                        }}
                      >
                        {intl.formatMessage(MSG.confirm)}
                      </Button>
                    </ButtonGroup>
                  )}
                </Flex>
                {(dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail) && (
                  <Button
                    w='100%'
                    variant='outline'
                    colorScheme='secondary'
                    bg='neutral.50'
                    mt={-4}
                    tracking={{
                      guid: '5TDGbTrJHZAL2jrUtSrRVi',
                      ui_context: 'Assumables direct message',
                      descriptor: 'Clicked on failure / success button'
                    }}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                )}
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}
